import { useCallback, useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';

import {
  TtqQuestionExplosionWorkbench,
  type TtqExplosionSelection,
} from '../../features/ttqib/TtqQuestionExplosionWorkbench';
import { TtqWorkbenchSourceParticle } from '../../features/ttqib/TtqWorkbenchSourceParticle';
import { TtqWorkbenchDerivedList, TtqWorkbenchFocusLayout } from '../../features/ttqib/TtqWorkbenchSplitLayout';
import { TtqWorkbenchToolbar, TtqWorkbenchToolbarAction } from '../../features/ttqib/TtqWorkbenchToolbar';
import { useTtqWorkbenchFocusEmphasis } from '../../features/ttqib/useTtqWorkbenchFocusEmphasis';
import { TtqWorkbenchShell, useTtqWorkbenchContext } from '../../features/ttqib/ttqWorkbenchShared';
import { TTQ_QUESTION_CHAIN_VARIANTS } from '../../features/ttqib/ttqBehaviorCatalog';
import {
  ttqWorkbenchService,
  type TtqWorkbenchDerivedItem,
} from '../../features/ttqib/ttqWorkbenchService';
import { fetchWorkbenchParticle } from '../../features/ttqib/ttqWorkbenchParticleCache';
import type { WorkspaceDerivedItem } from '../../services/kpWorkspaceService';

function toWorkspaceDerivedItems(items: TtqWorkbenchDerivedItem[]): WorkspaceDerivedItem[] {
  return items.map((item) => ({
    id: item.id,
    title: item.title || '变式题',
    summary: '',
    createdAt: item.createdAt || '',
    source: 'session' as const,
    question: item.question,
    answer: item.answer,
    particleId: item.particleId,
  }));
}

export function TtqQuestionChainWorkbenchPage() {
  const { ttqaId = '', particleId = '' } = useParams();
  const location = useLocation();
  const returnTo = `${location.pathname}${location.search}`;
  const ctx = useTtqWorkbenchContext(ttqaId, particleId);
  const [selection, setSelection] = useState<TtqExplosionSelection>({ segmentIds: [], variantId: null });
  const [generating, setGenerating] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [derived, setDerived] = useState<TtqWorkbenchDerivedItem[]>([]);
  const [usedVariantIds, setUsedVariantIds] = useState<Set<string>>(new Set());
  const split = derived.length > 0;
  const { emphasis, setEmphasis } = useTtqWorkbenchFocusEmphasis(split);

  const loadDerived = useCallback(async () => {
    try {
      const context = await ttqWorkbenchService.getQuestionChainContext(ttqaId, particleId);
      const previews = ttqWorkbenchService.mapDerivedPreviews(context);
      const enriched = await Promise.all(
        previews.map(async (item) => {
          const particle = await fetchWorkbenchParticle(item.particleId).catch(() => null);
          const question = typeof particle?.questions === 'string'
            ? particle.questions
            : particle?.questions && typeof particle.questions === 'object' && 'main' in particle.questions
              ? String((particle.questions as { main?: string }).main || '')
              : item.question;
          const answer = typeof particle?.answers === 'string'
            ? particle.answers
            : particle?.answers && typeof particle.answers === 'object' && 'main' in particle.answers
              ? String((particle.answers as { main?: string }).main || '')
              : item.answer;
          return {
            ...item,
            title: item.title || particle?.title || '变式题',
            question,
            answer,
          };
        }),
      );
      setDerived(enriched);
      setUsedVariantIds(new Set(
        (context.questionChainVariants || [])
          .filter((item) => item.alreadyUsed)
          .map((item) => item.id),
      ));
    } catch (e) {
      setActionError(e instanceof Error ? e.message : '加载变式列表失败');
    }
  }, [ttqaId, particleId]);

  useEffect(() => { void loadDerived(); }, [loadDerived]);

  const variantLabel = selection.variantId
    ? TTQ_QUESTION_CHAIN_VARIANTS.find((v) => v.id === selection.variantId)?.label
    : null;

  const handleGenerate = async () => {
    if (!selection.variantId) return;
    if (usedVariantIds.has(selection.variantId)) {
      setActionError('该 L2 变式已在工作台生成过');
      return;
    }
    setGenerating(true);
    setActionError(null);
    try {
      await ttqWorkbenchService.runQuestionChain(ttqaId, {
        sourceParticleIds: [particleId],
        questionChainVariantId: selection.variantId,
        batchSize: 1,
        anchorText: selection.anchorText || undefined,
      });
      await loadDerived();
      setSelection({ segmentIds: [], variantId: null });
    } catch (e) {
      setActionError(e instanceof Error ? e.message : '生成失败');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <TtqWorkbenchShell
      ttqaId={ttqaId}
      title="一题多题工作台"
      angleId="question_chain"
      loading={ctx.loading}
      error={ctx.error}
      restoreParticleIds={[particleId]}
      toolbar={(
        <TtqWorkbenchToolbar
          showFocusToggle={split}
          leftLabel="源题"
          rightLabel="变式"
          emphasis={emphasis}
          onEmphasisChange={setEmphasis}
        >
          <TtqWorkbenchToolbarAction
            disabled={!selection.variantId || generating}
            loading={generating}
            onClick={() => { void handleGenerate(); }}
          >
            正式发起变式
          </TtqWorkbenchToolbarAction>
        </TtqWorkbenchToolbar>
      )}
    >
      {actionError ? <p className="text-sm text-danger">{actionError}</p> : null}
      <TtqWorkbenchFocusLayout
        split={split}
        emphasis={emphasis}
        source={(
          <TtqWorkbenchSourceParticle
            particleId={particleId}
            particle={ctx.particle}
            particleLoading={ctx.loading}
            sourceImage={ctx.sourceImage}
            imageLoading={ctx.imageLoading}
            explosionSlot={(
              <TtqQuestionExplosionWorkbench
                questionText={ctx.questionText}
                selection={selection}
                onSelectionChange={setSelection}
                disabledVariantIds={[...usedVariantIds]}
              />
            )}
          />
        )}
        derived={<TtqWorkbenchDerivedList title="变式颗粒" items={toWorkspaceDerivedItems(derived)} emptyText="暂无变式题，选择题面锚点与 L2 变式后发起生成" returnTo={returnTo} />}
      />
      {variantLabel ? (
        <p className="mt-2 text-xs text-muted">已选 L2：{variantLabel}{selection.anchorText ? ` · 锚点「${selection.anchorText.slice(0, 40)}」` : ''}</p>
      ) : null}
    </TtqWorkbenchShell>
  );
}
